import { ParsedPolicyError } from 'api/requests/time-registration-policy-requests';
import { CompanyRole } from 'constants/CompanyRole';
import { formatUnitValue, pickUnitFields } from 'modules/management/time-registration-policy/helpers';
import { UnitDefinition } from 'modules/management/time-registration-policy/units';
import {
  TIME_REGISTRATION_POLICY__REQUIRED_FIELD,
  TIME_REGISTRATION_POLICY__UNKNOWN_SETTING,
  TIME_REGISTRATION_SETTINGS__ALLOW_REGISTRATION_BEFORE_PLANNED_START_LOCKED,
  TIME_REGISTRATION_SETTINGS__ALLOW_START_IN_FUTURE_LOCKED,
  TIME_REGISTRATION_SETTINGS__AUTO_FREEZE_ON_CLOCK_OUT_LOCKED,
  TIME_REGISTRATION_SETTINGS__BREAK_ROUNDING_LOCKED,
  TIME_REGISTRATION_SETTINGS__EXTENDED_REGISTRATION_WINDOW_LOCKED,
  TIME_REGISTRATION_SETTINGS__FEATURE_NOT_ENABLED,
  TIME_REGISTRATION_SETTINGS__KIOSK_AUTHENTICATION_MODE_LOCKED,
  TIME_REGISTRATION_SETTINGS__KIOSK_MODE_AVAILABILITY_LOCKED,
  TIME_REGISTRATION_SETTINGS__LOCATION_CAPTURE_LOCKED,
  TIME_REGISTRATION_SETTINGS__LOCATION_IN_MULTIPLE_OVERRIDES,
  TIME_REGISTRATION_SETTINGS__LOCATION_OVERRIDE_NOT_ALLOWED,
  TIME_REGISTRATION_SETTINGS__LOCATION_OVERRIDE_WITHOUT_LOCATIONS,
  TIME_REGISTRATION_SETTINGS__LOCATION_OVERRIDES_COUNT_ONE,
  TIME_REGISTRATION_SETTINGS__LOCATION_OVERRIDES_COUNT_OTHER,
  TIME_REGISTRATION_SETTINGS__MAXIMUM_NUMBER_OF_BREAKS_LOCKED,
  TIME_REGISTRATION_SETTINGS__OVERWORK_LIMIT_LOCKED,
  TIME_REGISTRATION_SETTINGS__PERSONAL_DEVICE_BLOCK_LOCKED,
  TIME_REGISTRATION_SETTINGS__REGISTRATION_WINDOW_LOCKED,
  TIME_REGISTRATION_SETTINGS__START_END_ROUNDING_LOCKED,
  TIME_REGISTRATION_SETTINGS__STATE_LOCKED_TEXT,
  TIME_REGISTRATION_SETTINGS__STATE_OVERRULABLE_TEXT,
  TIME_REGISTRATION_SETTINGS__STATE_OVERRULED_TEXT,
  TIME_REGISTRATION_SETTINGS__UNKNOWN_LOCATION,
} from 'translations/constants';
import {
  CompanyPolicySaveResult,
  CompanyUnitSetting,
  CompanyUnitState,
  CompanyUnitStatus,
  LocationOverride,
  OverrideErrors,
} from 'types/company/time-registration/ICompanyTimeRegistrationPolicy';
import { PolicyUnit } from 'types/time-registration-policy/ITimeRegistrationPolicy';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const UNIT_LOCKED_KEYS: Record<PolicyUnit, string> = {
  [PolicyUnit.AllowRegistrationBeforePlannedStart]:
    TIME_REGISTRATION_SETTINGS__ALLOW_REGISTRATION_BEFORE_PLANNED_START_LOCKED,
  [PolicyUnit.AllowStartInFuture]: TIME_REGISTRATION_SETTINGS__ALLOW_START_IN_FUTURE_LOCKED,
  [PolicyUnit.RegistrationWindow]: TIME_REGISTRATION_SETTINGS__REGISTRATION_WINDOW_LOCKED,
  [PolicyUnit.ExtendedRegistrationWindow]: TIME_REGISTRATION_SETTINGS__EXTENDED_REGISTRATION_WINDOW_LOCKED,
  [PolicyUnit.MaximumNumberOfBreaks]: TIME_REGISTRATION_SETTINGS__MAXIMUM_NUMBER_OF_BREAKS_LOCKED,
  [PolicyUnit.BreakRounding]: TIME_REGISTRATION_SETTINGS__BREAK_ROUNDING_LOCKED,
  [PolicyUnit.StartEndRounding]: TIME_REGISTRATION_SETTINGS__START_END_ROUNDING_LOCKED,
  [PolicyUnit.OverworkLimit]: TIME_REGISTRATION_SETTINGS__OVERWORK_LIMIT_LOCKED,
  [PolicyUnit.AutoFreezeOnClockOut]: TIME_REGISTRATION_SETTINGS__AUTO_FREEZE_ON_CLOCK_OUT_LOCKED,
  [PolicyUnit.KioskAuthenticationMode]: TIME_REGISTRATION_SETTINGS__KIOSK_AUTHENTICATION_MODE_LOCKED,
  [PolicyUnit.PersonalDeviceBlock]: TIME_REGISTRATION_SETTINGS__PERSONAL_DEVICE_BLOCK_LOCKED,
  [PolicyUnit.KioskModeAvailability]: TIME_REGISTRATION_SETTINGS__KIOSK_MODE_AVAILABILITY_LOCKED,
  [PolicyUnit.LocationCapture]: TIME_REGISTRATION_SETTINGS__LOCATION_CAPTURE_LOCKED,
};

export const STATUS_HELP_KEYS: Record<CompanyUnitStatus, string> = {
  overrulable: TIME_REGISTRATION_SETTINGS__STATE_OVERRULABLE_TEXT,
  overruled: TIME_REGISTRATION_SETTINGS__STATE_OVERRULED_TEXT,
  locked: TIME_REGISTRATION_SETTINGS__STATE_LOCKED_TEXT,
};

const KNOWN_SAVE_ERROR_KEYS = [
  TIME_REGISTRATION_SETTINGS__FEATURE_NOT_ENABLED,
  TIME_REGISTRATION_SETTINGS__LOCATION_OVERRIDE_NOT_ALLOWED,
  TIME_REGISTRATION_SETTINGS__LOCATION_OVERRIDE_WITHOUT_LOCATIONS,
  TIME_REGISTRATION_SETTINGS__LOCATION_IN_MULTIPLE_OVERRIDES,
  TIME_REGISTRATION_SETTINGS__UNKNOWN_LOCATION,
  TIME_REGISTRATION_POLICY__UNKNOWN_SETTING,
];

const INVALID_KEY_PREFIX = '[TIME_REGISTRATION_POLICY] INVALID_';
const LOCKED_KEY_SUFFIX = '_LOCKED';

export const getUnitStatus = (state: CompanyUnitState): CompanyUnitStatus => {
  if (state.locked) return 'locked';
  return state.overruled ? 'overruled' : 'overrulable';
};

export const canEditUnit = (state: CompanyUnitState, role: CompanyRole): boolean =>
  role === CompanyRole.ADMIN && !state.locked;

export const hasLocationOverrides = (def: UnitDefinition): boolean =>
  def.unit === PolicyUnit.KioskAuthenticationMode || def.unit === PolicyUnit.PersonalDeviceBlock;

export const toSetting = (state: CompanyUnitState): CompanyUnitSetting => ({
  value: state.value,
  overruled: state.overruled,
  locationOverrides: state.locationOverrides ?? [],
});

const normalizeOverrides = (def: UnitDefinition, overrides: LocationOverride[]): LocationOverride[] =>
  overrides.map(override => ({
    value: pickUnitFields(def, override.value),
    locationIds: [...override.locationIds].sort((a, b) => a - b),
  }));

export const haveOverridesChanged = (
  def: UnitDefinition,
  stored: LocationOverride[],
  draft: LocationOverride[],
): boolean => JSON.stringify(normalizeOverrides(def, stored)) !== JSON.stringify(normalizeOverrides(def, draft));

export const validateOverrides = (def: UnitDefinition, overrides: LocationOverride[]): OverrideErrors[] =>
  overrides.map(override => {
    const errors: OverrideErrors = {};
    if (def.kind === 'authMode' && !override.value.authenticationMode) {
      errors.authenticationMode = { key: TIME_REGISTRATION_POLICY__REQUIRED_FIELD };
    }
    if (!override.locationIds.length) {
      errors.locationIds = { key: TIME_REGISTRATION_SETTINGS__LOCATION_OVERRIDE_WITHOUT_LOCATIONS };
    }
    return errors;
  });

export const hasOverrideErrors = (errors: OverrideErrors[]): boolean =>
  errors.some(error => Object.keys(error).length > 0);

export const mapCompanySaveError = (parsed: ParsedPolicyError): CompanyPolicySaveResult => {
  if (parsed.timedOut) return { status: 'timeout' };
  if (parsed.status === 409 && parsed.key?.endsWith(LOCKED_KEY_SUFFIX)) return { status: 'locked' };
  if (parsed.status === 409) return { status: 'conflict' };
  if (parsed.status === 400 && parsed.key?.startsWith(INVALID_KEY_PREFIX) && parsed.details.field) {
    const { field, min, max } = parsed.details;
    const params = min !== undefined && max !== undefined ? { min, max } : undefined;
    return { status: 'invalid', fieldErrors: { [field]: { key: parsed.key, params } } };
  }
  return { status: 'error', messageKey: KNOWN_SAVE_ERROR_KEYS.includes(parsed.key) ? parsed.key : null };
};

export const formatCompanyUnitValue = (def: UnitDefinition, state: CompanyUnitState, t: Translate): string => {
  const value = formatUnitValue(def, state.value, t);
  const count = state.locationOverrides?.length ?? 0;
  if (!count) return value;
  return t(
    count === 1
      ? TIME_REGISTRATION_SETTINGS__LOCATION_OVERRIDES_COUNT_ONE
      : TIME_REGISTRATION_SETTINGS__LOCATION_OVERRIDES_COUNT_OTHER,
    { value, count },
  );
};
